import { getCurrentSubscription, CurrentSubscription } from './subscriptionApi';

interface CachedSubscription {
  data: CurrentSubscription;
  timestamp: number;
  isStale: boolean;
}

// Cache storage
const subscriptionCache = new Map<string, CachedSubscription>();

// Cache duration: 1 minute
const CACHE_DURATION = 60 * 1000; // 60 seconds

// Background update interval: 1 minute
const BACKGROUND_UPDATE_INTERVAL = 60 * 1000; // 60 seconds

// Track background update intervals per user
const backgroundUpdateIntervals = new Map<string, NodeJS.Timeout>();

/**
 * Get cached subscription or fetch fresh data
 */
export async function getCachedSubscription(userId: string): Promise<CurrentSubscription> {
  const cached = subscriptionCache.get(userId);
  const now = Date.now();

  // If we have a valid cache, return it
  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    return cached.data;
  }

  // If we have stale cache, return it immediately and update in background
  if (cached && cached.isStale) {
    updateSubscriptionInBackground(userId);
    return cached.data;
  }

  // No cache or expired cache, fetch fresh data
  try {
    const subscription = await getCurrentSubscription();
    
    // Cache the fresh data
    subscriptionCache.set(userId, {
      data: subscription,
      timestamp: now,
      isStale: false
    });

    // Start background updates
    startBackgroundUpdates(userId);

    return subscription;
  } catch (error) {
    console.error('❌ [subscriptionCache] Error fetching subscription:', error);
    
    // If we have any cache (even expired), return it as fallback
    if (cached) {
      return cached.data;
    }
    
    // No cache available, re-throw the error
    throw error;
  }
}

/**
 * Update subscription in background without blocking the UI
 */
async function updateSubscriptionInBackground(userId: string): Promise<void> {
  try {
    const subscription = await getCurrentSubscription();
    
    subscriptionCache.set(userId, {
      data: subscription,
      timestamp: Date.now(),
      isStale: false
    });
    
    // Dispatch event to notify components of subscription update
    window.dispatchEvent(new CustomEvent('subscription-updated', {
      detail: { subscription, userId }
    }));
  } catch (error) {
    console.error('❌ [subscriptionCache] Background subscription update failed:', error);
    
    // Mark current cache as stale so we know to retry
    const cached = subscriptionCache.get(userId);
    if (cached) {
      cached.isStale = true;
    }
  }
}

/**
 * Start background updates for a user
 */
function startBackgroundUpdates(userId: string): void {
  // Clear existing interval if any
  const existingInterval = backgroundUpdateIntervals.get(userId);
  if (existingInterval) {
    clearInterval(existingInterval);
  }

  // Start new background update interval
  const interval = setInterval(() => {
    updateSubscriptionInBackground(userId);
  }, BACKGROUND_UPDATE_INTERVAL);

  backgroundUpdateIntervals.set(userId, interval);
}

/**
 * Stop background updates for a user
 */
export function stopBackgroundUpdates(userId: string): void {
  const interval = backgroundUpdateIntervals.get(userId);
  if (interval) {
    clearInterval(interval);
    backgroundUpdateIntervals.delete(userId);
  }
}

/**
 * Clear cache for a specific user
 */
export function clearUserCache(userId: string): void {
  subscriptionCache.delete(userId);
  stopBackgroundUpdates(userId);
}

/**
 * Clear all caches (useful for logout)
 */
export function clearAllCaches(): void {
  subscriptionCache.clear();
  
  // Clear all background intervals
  backgroundUpdateIntervals.forEach((interval) => {
    clearInterval(interval);
  });
  backgroundUpdateIntervals.clear();
}

/**
 * Force refresh subscription (bypass cache)
 */
export async function forceRefreshSubscription(userId: string): Promise<CurrentSubscription | null> {
  try {
    const subscription = await getCurrentSubscription();
    if (subscription) {
      subscriptionCache.set(userId, {
        data: subscription,
        timestamp: Date.now(),
        isStale: false
      });
    }
    return subscription;
  } catch (error) {
    console.error('Force refresh failed:', error);
    return null;
  }
}

/**
 * Get cache status for debugging
 */
export function getCacheStatus(userId: string): {
  hasCache: boolean;
  isStale: boolean;
  age: number | null;
  hasBackgroundUpdates: boolean;
} {
  const cached = subscriptionCache.get(userId);
  const now = Date.now();
  
  return {
    hasCache: !!cached,
    isStale: cached?.isStale || false,
    age: cached ? now - cached.timestamp : null,
    hasBackgroundUpdates: backgroundUpdateIntervals.has(userId)
  };
}
